import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { AccountDisplay } from '~/components/ui/AccountDisplay';
import { AutoSuggestInput } from '~/components/ui/AutoSuggestInput/AutoSuggestInput';
import { Button } from '~/components/ui/Button';
import { CustomSelect } from '~/components/ui/CustomSelect';
import { sendInvites } from '~/lib/.client/api/organizations';
import { getUsers } from '~/lib/users/usersApi';
import {
  allOrganizationUserRoles,
  type OrganizationData,
  type OrganizationMembership,
  type OrganizationUserRoles,
} from '~/types/organization';
import type { UserData, UserSuggestion } from '~/types/user';
import { debounce } from '~/utils/debounce';
import { withSpinner } from '~/utils/withSpinner';

const FETCH_USERS_DEBOUNCE = 300;

export const SendInviteField = ({
  organization,
  refetchMembers,
  currentMembers,
}: {
  organization: OrganizationData;
  refetchMembers: VoidFunction;
  currentMembers: OrganizationMembership[];
}) => {
  const [isSending, setIsSending] = useState(false);

  const [userSuggestions, setUserSuggestions] = useState<UserSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const [userInput, setUserInput] = useState('');

  const [invitee, setInvitee] = useState<{ username: string; role: OrganizationUserRoles }>({
    username: '',
    role: 'member',
  });

  const fetchUsers = useCallback(
    async (username: string) => {
      await withSpinner({
        action: async () => {
          const users = await getUsers({ username });

          if (users) {
            const filteredInvitees = users.filter(
              (user) => !currentMembers.some((member) => member.user.username === user.username),
            );
            setUserSuggestions(filteredInvitees);
          }
        },
        onStateUpdate: setIsLoadingSuggestions,
      });
    },
    [currentMembers],
  );

  const debouncedFetchUsers = useMemo(
    () => debounce((username: string) => fetchUsers(username), FETCH_USERS_DEBOUNCE),
    [fetchUsers],
  );

  const sendInvite = () => {
    withSpinner({
      action: async () => {
        try {
          await sendInvites({
            organization,
            members: [
              userInput.includes('@')
                ? {
                    email: userInput,
                    role: invitee.role,
                  }
                : {
                    username: invitee.username,
                    role: invitee.role,
                  },
            ],
          });
          refetchMembers();
          setUserInput('');
          setInvitee({
            username: '',
            role: invitee.role,
          });
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }
        }
      },
      onStateUpdate: setIsSending,
    });
  };

  return (
    <div className="flex flex-wrap gap-4 items-start">
      <div className="relative max-w-[300px] sm:max-w-unset sm:grow-1">
        <AutoSuggestInput
          suggestions={userSuggestions}
          focusInputOnSuggestionClick={false}
          onSuggestionsFetchRequested={({ value }) => debouncedFetchUsers(value)}
          highlightFirstSuggestion
          onSuggestionsClearRequested={() => setUserSuggestions([])}
          getSuggestionValue={(user: UserSuggestion) => user.username}
          onSuggestionSelected={(_, { suggestion }) => {
            setInvitee({ username: suggestion.username, role: invitee.role });
          }}
          renderSuggestion={(user) => (
            <AccountDisplay
              user={user as UserData}
              title={user.username}
              description={user.name ?? null}
              accentLine="first"
            />
          )}
          inputProps={{
            placeholder: 'Enter a username or email...',
            value: userInput,
            className: 'pl-7 pr-20 h-11 sm:pr-30',
            onChange: (_, { newValue }) => {
              setUserInput(newValue);
            },
            onKeyUp: (e) => {
              if (e.key === 'Enter' && userInput.includes('@')) {
                sendInvite();
              }
            },
          }}
          isLoading={isLoadingSuggestions}
        />
        <CustomSelect
          triggerNode={invitee.role.replaceAll('_', ' ')}
          items={allOrganizationUserRoles.map((role) => ({
            label: role.replaceAll('_', ' '),
            value: role,
          }))}
          onSelect={(role) => {
            setInvitee({ username: invitee.username, role: role as OrganizationUserRoles });
          }}
          itemClassName="capitalize"
          triggerClassName="p-0 absolute right-3 top-2.5 focus:border-none text-bolt-ds-textSecondary"
        />
      </div>
      <Button
        className="h-11"
        variant="secondary"
        disabled={isSending || (invitee.username.length === 0 && !userInput.includes('@'))}
        onClick={sendInvite}
        loading={isSending}
      >
        Send invite
      </Button>
    </div>
  );
};
